// TSK-5163: Cancel Order dialog for an Advance / Pre-Order, shared by the retail
// (SupermarketPreorder/ManagePreorder) and restaurant (restaurantPreorder/Preorder) screens.
// All checks and writes are in utils/advanceOrderCancel.runAdvanceOrderCancel.
import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import db from "../../database";
import { CANCEL_MESSAGES, runAdvanceOrderCancel } from "../../utils/advanceOrderCancel";

const readOnline = () => (typeof navigator === "undefined" ? true : navigator.onLine !== false);

// order: one salesHistory record (getSalesHistoryData(...)[0]).
// currency: tillData.tillAccess.csBunit.currencies[0].
// onCancelled(result): called once the server has cancelled the order.
const AdvanceOrderCancelModal = ({ open, order, currency, onClose, onCancelled }) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState(readOnline);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const update = () => setOnline(readOnline());
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setReason("");
      setOnline(readOnline());
    }
  }, [open]);

  const symbol = currency?.currSymbol || "";
  const withSymbol = (value) => (
    <>
      {currency?.symbolRightSide === "N" ? symbol : ""}
      {value}
      {currency?.symbolRightSide === "Y" ? symbol : ""}
    </>
  );
  const advancePaid = order?.advancePayment;

  const close = () => {
    if (inFlightRef.current) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (inFlightRef.current || !order?.sOrderID) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const result = await runAdvanceOrderCancel({
        sOrderID: order.sOrderID,
        reason,
        db,
        isOnline: readOnline(),
        currencySymbol: symbol || "Rs.",
      });
      if (!result.ok) {
        message.error(result.message);
        return;
      }
      if (result.level === "warning") message.warning(result.message, 8);
      else message.success(result.message);
      onCancelled?.(result);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      destroyOnHidden={true}
      title={null}
      footer={null}
      width={450}
      centered={true}
      maskClosable={!loading}
      style={{ borderRadius: "20px" }}
      styles={{ body: { padding: "25px", minHeight: advancePaid > 0 ? "440px" : "330px" } }}
      closeIcon={<CloseOutlined style={{ fontSize: "16px", color: "#666" }} />}
    >
      <div>
        <h2 style={{ color: "#dc3545", fontSize: "16px", fontWeight: "600", marginTop: 0 }}>Cancel Order</h2>

        <div
          style={{
            backgroundColor: "rgb(254 242 242 / var(--tw-bg-opacity, 1))",
            border: "1px solid rgb(254 202 202 / var(--tw-border-opacity, 1))",
            borderRadius: "8px",
            padding: "14px",
            marginBottom: "14px",
          }}
        >
          <div style={{ color: "rgb(185 28 28 / var(--tw-text-opacity, 1))", fontSize: "13px", lineHeight: "1.5" }}>
            <div style={{ color: "rgb(153 27 27 / var(--tw-text-opacity, 1))", fontSize: "13px", marginTop: 0 }}>Order Details</div>
            <div>Order ID: {order?.documentno}</div>
            <div>Customer: {order?.customer?.name || ""}</div>
            <div>Advance Paid: {withSymbol(advancePaid || "0.00")}</div>
          </div>
        </div>

        <div>
          <label style={{ display: "block", color: "#333", fontSize: "13px", fontWeight: "500", marginBottom: "8px" }}>Cancellation Reason</label>
          <Input.TextArea
            placeholder="Enter reason for cancellation..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            disabled={loading}
            style={{ fontSize: "12px", borderRadius: "8px", marginBottom: "14px" }}
          />
        </div>
        {advancePaid > 0 && (
          <div
            style={{
              backgroundColor: "rgb(254 252 232 / var(--tw-bg-opacity, 1))",
              border: "1px solid rgb(254 240 138 / var(--tw-border-opacity, 1))",
              borderRadius: "8px",
              padding: "8px",
              marginBottom: "14px",
            }}
          >
            <div style={{ color: "#856404", fontSize: "13px", lineHeight: "1.5" }}>
              <strong>Note:</strong> This will cancel the order and refund the advance paid in cash from this till.
            </div>
          </div>
        )}
        {!online && (
          <div data-testid="advance-cancel-offline" style={{ color: "#dc3545", fontSize: "12px", marginBottom: "10px" }}>
            {CANCEL_MESSAGES.offline}
          </div>
        )}

        <div style={{ display: "flex", gap: "12px", width: "100%" }}>
          <Button style={{ borderRadius: "6px", fontWeight: "500", width: "50%", fontSize: "12px", height: "40px" }} disabled={loading} onClick={close}>
            Keep Order
          </Button>
          <Button
            type="primary"
            danger
            loading={loading}
            disabled={!online || loading}
            style={{ borderRadius: "6px", fontWeight: "500", width: "50%", fontSize: "12px", height: "40px" }}
            onClick={handleConfirm}
          >
            {advancePaid > 0 ? "Cancel & Refund" : "Cancel Order"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AdvanceOrderCancelModal;
